import React, { useState, useRef, useEffect } from 'react';
import { Bot, Send } from 'lucide-react';
import '../styles/components/AIAssistant.css';

const AIAssistant = ({ chatMessages = [], sendMessage = () => { } }) => {
   const [newMessage, setNewMessage] = useState('');
   const messagesEndRef = useRef(null);

   const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
   };

   useEffect(() => {
      scrollToBottom();
   }, [chatMessages]);

   const handleSendMessage = () => {
      if (newMessage.trim()) {
         sendMessage(newMessage);
         setNewMessage('');
      }
   };

   const handleKeyPress = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
         e.preventDefault();
         handleSendMessage();
      }
   };

   const quickSuggestions = [
      "What should I wear for a coffee date tomorrow?",
      "How can I style my white button down?",
      "Outfit ideas for work meetings",
      "What goes with black jeans?",
      "Summer styling tips",
      "Date night outfit suggestions"
   ];

   const sampleConversation = [
      {
         id: 1,
         sender: 'AI Fashion Assistant',
         message: 'Hello! I\'m your personal fashion assistant. I can help you with outfit suggestions, styling tips, and how to mix and match your saved pieces!',
         timestamp: '2:30 PM'
      }
   ];

   const displayMessages = chatMessages.length > 0 ? chatMessages : sampleConversation;

   return (
      <div className="ai-assistant-container">
         {/* Header */}
         <div className="ai-header">
            <div className="ai-avatar">
               <Bot size={20} />
            </div>
            <div className="ai-info">
               <h3 className="ai-name">AI Fashion Assistant</h3>
               <p className="ai-status">Get personalized style advice</p>
            </div>
         </div>

         {/* Messages */}
         <div className="messages-container">
            {displayMessages.length <= 1 && (
               <div className="quick-suggestions">
                  <p className="suggestions-title">Try asking about:</p>
                  <div className="suggestions-grid">
                     {quickSuggestions.map((suggestion, index) => (
                        <button
                           key={index}
                           onClick={() => setNewMessage(suggestion)}
                           className="suggestion-btn"
                        >
                           {suggestion}
                        </button>
                     ))}
                  </div>
               </div>
            )}

            <div className="messages-list">
               {displayMessages.map(msg => (
                  <div key={msg.id} className={`message ${msg.sender === 'You' ? 'user' : 'assistant'}`}>
                     <div className={`message-bubble ${msg.sender === 'You' ? 'user' : 'assistant'}`}>
                        <p className="message-text">{msg.message}</p>
                        <p className="message-timestamp">{msg.timestamp}</p>
                     </div>
                  </div>
               ))}
               <div ref={messagesEndRef} />
            </div>
         </div>

         {/* Input */}
         <div className="chat-input-container">
            <div className="input-wrapper">
               <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about styling, colors, occasions..."
                  className="chat-input"
                  maxLength={500}
               />
               <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  className="send-button"
               >
                  <Send size={18} />
               </button>
            </div>
            <div className="input-footer">
               <p className="character-count">{newMessage.length}/500</p>
            </div>
         </div>

         {/* Fashion Tips */}
         <div className="fashion-tips">
            <div className="tips-header">
               <h4>💡 Fashion Tips</h4>
            </div>
            <div className="tips-content">
               <p>• Based on your saved outfits, I can suggest similar styles</p>
               <p>• I analyze weather and occasion for perfect recommendations</p>
               <p>• Ask about mixing and matching items from your closet</p>
            </div>
         </div>
      </div>
   );
};

export default AIAssistant;